import React, { useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router';
import { isEmpty } from 'lodash';
import { Form } from 'react-final-form';
import { useToggle, useEffectOnce } from 'react-use';
import axios from 'axios';
import styled from 'styled-components';
import { AutosaveEffect } from '../../hooks/useAutosave';
import useSaveAction from '../../hooks/useSaveAction';
import dayjs from '../../helpers/dates';
import {
  Container,
  Card,
  Row,
  Column,
  Flex,
  Text,
  Button,
  Box,
} from '../../design-system';
import Alert from '../../helpers/alert';
import {
  useData,
  successRefetch,
  isAccessDenied,
} from '../../helpers/data-loader';

import {
  errorMsg,
  prettyPhone,
  currencyFormatter,
  formatEIN,
  checkPermissions,
} from '../../helpers/utils';
import Attachments from '../../components/Attachments';
import FieldColumn from '../../components/FieldColumn';
import ModalDelete from '../../components/ModalDelete';
import NotAccessible from '../../components/NotAccessible';
import ReadOnlyColumn from '../../components/ReadOnlyColumn';
import { Table } from '../../components/Table';
import {
  COMPLETED_LIST,
  SUCCESS_MSG,
  ERR_MSG,
  PERMISSIONS,
  STATE_OPTIONS,
  COUNTRY_OPTIONS,
  US_STATES,
} from '../../globalConstants';
import { FUNDER_PROJECTS_COLUMNS, FIELDS } from './constants';
import { Session } from '../../contexts/sessionContext';
import CardHeader from '../../components/CardHeader';
import ContactList from '../../components/ContactList';
import AddProjectModal from '../../components/AddProjectModal';

const Quantity = styled.h1`
  padding: 0;
  margin: 0;
`;

const StyledBox = styled(Box)`
  text-align: center;
`;

const StyledFlex = styled(Flex)`
  overflow-x: auto;
  padding: 0;
`;

const FunderEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    state: {
      user: { permissions, isClient },
    },
  } = useContext(Session);

  const [lastSave, setLastSave] = useState<any>(null);
  const canEdit = checkPermissions(permissions, [PERMISSIONS.UPDATE_FUNDERS]);
  const [isDeleteFunderModalOpen, toggleDeleteFunderModal] = useToggle(false);
  const [attachmentsModal, toggleAttachmentsModal] = useState(false);
  const [isEditing, setEditingStatus] = useState(false);
  const [funder, fetchFunderData, funderLoading, error] = useData(
    `/funder/${id}`,
    {
      errorMsg: errorMsg('funder'),
    },
  );

  const [clientData, fetchClientData] = useData('/client', {
    errorMsg: errorMsg('clients'),
  });

  const [funderData, fetchAllFunderData] = useData('/funder?simple=1', {
    errorMsg: errorMsg('funders'),
  });

  const {
    name,
    phone,
    fax,
    tax_id,
    visible_projects,
    access_level,
    updated_at: updatedAt,
  } = funder;
  const modelId = id;
  const modelType = 'AppModelsFunder';

  let visible_projects_list = [];

  if (visible_projects) {
    visible_projects_list = visible_projects;
  }
  const toggleEditing = () => {
    setEditingStatus(!isEditing);
  };

  // @TODO: Need to refactor useData like Voyage. That way we can use it as a POST request as well to set the data here.
  const [onSubmit, saving] = useSaveAction(async (values: any) => {
    const payload = {
      id: values.id,
      name: values.name,
      tax_id: values.tax_id,
      type: values.type,
      phone: values.phone,
      fax: values.fax,
      email: values.email,
      address: values.address,
      city: values.city,
      state: values.state,
      country: values.country,
      zip: values.zip,
      website: values.website,
      notes: values.notes,
      deadline_type: values.deadline_type,
    };
    await axios.put('/funder/update', payload);
    setLastSave(dayjs());
  });

  const finishEditing = () => {
    toggleEditing();
    successRefetch(fetchFunderData);
  };

  const handleDeleteFunder = async () => {
    try {
      await axios.delete(`/funder/${funder.id}`);
      toggleDeleteFunderModal();
      navigate('/funders', { replace: true });
    } catch {
      Alert.error(ERR_MSG);
    }
  };

  useEffectOnce(() => {
    fetchClientData();
    fetchFunderData();
    fetchAllFunderData();
  });

  const averageProjectAward = () => {
    let [totalAwarded, count] = [0, 0];

    if (visible_projects_list && visible_projects_list.length) {
      visible_projects_list.forEach((project) => {
        if (
          project.amount_awarded &&
          project.status &&
          project.status === 'Awarded'
        ) {
          totalAwarded += parseInt(project.amount_awarded, 10);
          count += 1;
        }
      });
    }

    return count > 0 ? totalAwarded / count : 0;
  };

  const declinedProjects = () =>
    visible_projects_list.filter((project) => project.status === 'Declined')
      .length;
  const awardedProjects = () =>
    visible_projects_list.filter((project) => project.status === 'Awarded')
      .length;
  const pendingProjects = () =>
    visible_projects_list.filter((project) => project.status === 'Pending')
      .length;
  const openProjects = () =>
    visible_projects_list.filter(
      (project) => COMPLETED_LIST.indexOf(project.status) === -1,
    ).length;

  if (isAccessDenied(error)) {
    return (
      <NotAccessible
        text="You don't have access to this funder."
        redirect="/funders"
      />
    );
  }

  if (error && error.code === 405) {
    return <NotAccessible text="You are not permitted to view this page." />;
  }

  return (
    <Container pb={40} fluid>
      <Flex mt={20} justifyContent="space-between" alignItems="center">
        <Text id="funder-page-title" fontSize={25} fontWeight={600}>
          {funder && name}
        </Text>
        {clientData && funderData && (
          <AddProjectModal
            initialValues={{ funder: parseInt(id, 10) }}
            clientData={clientData}
            funderData={funderData}
          />
        )}
      </Flex>
      {!isEmpty(funder) && !funderLoading && (
        <>
          <Flex minHeight={500}>
            <Box mr={3} width="65%">
              <Card mt={20} height="100%">
                <CardHeader
                  title="General Info"
                  updatedAt={lastSave || updatedAt}
                  saving={saving}
                  isSmall
                />
                <Card.Body>
                  <Form
                    initialValues={{
                      country: 'United States',
                      ...funder,
                      state:
                        US_STATES.find(
                          ({ abbreviation }) => abbreviation === funder.state,
                        )?.name || funder.state,
                      phone: phone && prettyPhone(phone),
                      fax: fax && prettyPhone(fax),
                      tax_id: tax_id && formatEIN(tax_id),
                    }}
                    onSubmit={onSubmit}
                    render={({ handleSubmit, values }) => (
                      <form onSubmit={handleSubmit}>
                        <Row>
                          {FIELDS.map((field) => {
                            if (field.name === 'empty') {
                              return <Column key={field.name} width={325} />;
                            }
                            return canEdit && isEditing ? (
                              <FieldColumn
                                id={`${field.label.replace(
                                  /(\s|\/)/g,
                                  '',
                                )}-edit-field`}
                                key={field.name}
                                width={325}
                                {...field}
                                {...(field.name === 'state' && {
                                  options: values.country
                                    ? STATE_OPTIONS(values.country)
                                    : [],
                                })}
                                {...(field.name === 'country' && {
                                  options: COUNTRY_OPTIONS,
                                })}
                              />
                            ) : (
                              <ReadOnlyColumn
                                key={field.name}
                                data={funder}
                                field={field}
                                width={325}
                              />
                            );
                          })}
                        </Row>
                        {canEdit &&
                          (isEditing ? (
                            <>
                              <AutosaveEffect
                                data={values}
                                onSave={handleSubmit}
                                interval={200}
                              />
                              <Flex mt={20} justifyContent="flex-end">
                                <Button
                                  id="exit-edit-button"
                                  title="Update"
                                  aria-label="Finish Editing Funder"
                                  onClick={finishEditing}
                                  loading={saving}
                                >
                                  Update
                                </Button>
                              </Flex>
                            </>
                          ) : (
                            <Flex mt={20} justifyContent="flex-end">
                              <Button
                                id="enter-edit-button"
                                title="Edit Funder"
                                aria-label="Edit Funder"
                                type="button"
                                onClick={toggleEditing}
                              >
                                Edit
                              </Button>
                              {!isClient && (
                                <Button
                                  id="delete-funder-button"
                                  ml={20}
                                  title="Delete"
                                  aria-label="Delete Funder"
                                  type="button"
                                  variant="primaryText"
                                  onClick={toggleDeleteFunderModal}
                                >
                                  Delete
                                </Button>
                              )}
                            </Flex>
                          ))}
                      </form>
                    )}
                  />
                </Card.Body>
              </Card>
            </Box>
            <Box flex={1}>
              <Card mt={20} height="100%">
                <Card.Header mb={10} fontSize={18}>
                  Projects
                </Card.Header>
                <Card.Body>
                  <Flex>
                    <StyledBox m={3} p={3} flex={2} mt={0} pt={0}>
                      <Quantity>{visible_projects_list.length}</Quantity>
                      <span>Total Projects</span>
                    </StyledBox>
                    <StyledBox m={3} p={3} flex={2} mt={0} pt={0}>
                      <Quantity>
                        {currencyFormatter.format(averageProjectAward())}
                      </Quantity>
                      <span>Average Award</span>
                    </StyledBox>
                  </Flex>
                  <Flex>
                    <StyledBox m={3} p={3} flex={2} mt={0} pt={0}>
                      <Quantity>{declinedProjects()}</Quantity>
                      <span>Declined</span>
                    </StyledBox>
                    <StyledBox m={3} p={3} flex={2} mt={0} pt={0}>
                      <Quantity>{awardedProjects()}</Quantity>
                      <span>Awarded</span>
                    </StyledBox>
                  </Flex>
                  <Flex mb={10}>
                    <StyledBox m={3} p={3} flex={2} mt={0} pt={0}>
                      <Quantity>{pendingProjects()}</Quantity>
                      <span>Pending</span>
                    </StyledBox>
                    <StyledBox m={3} p={3} flex={2} mt={0} pt={0}>
                      <Quantity>{openProjects()}</Quantity>
                      <span>Open</span>
                    </StyledBox>
                  </Flex>
                  <StyledFlex>
                    <div className="datetime-relative">
                      <Table
                        name="Projects"
                        columns={FUNDER_PROJECTS_COLUMNS}
                        data={visible_projects_list}
                      />
                    </div>
                  </StyledFlex>
                </Card.Body>
              </Card>
            </Box>
          </Flex>
          {(access_level === 'org_admin' || access_level === 'superadmin') && (
            <Row mt={25} mb={60}>
              <Column>
                <ContactList
                  modelType="funder"
                  modelID={funder.id}
                  assignedContacts={funder.assignedContacts || []}
                  allContacts={funder.allContacts || []}
                  refetch={fetchFunderData}
                  canEdit={canEdit}
                />
              </Column>
              <Column>
                <Attachments
                  data={funder.files || []}
                  modelId={modelId}
                  modelType={modelType}
                  refetch={fetchFunderData}
                  disabled={!canEdit}
                  canEdit={canEdit}
                />
              </Column>
            </Row>
          )}
        </>
      )}
      <ModalDelete
        entity="funder"
        open={isDeleteFunderModalOpen}
        onCancel={toggleDeleteFunderModal}
        onDelete={handleDeleteFunder}
      />
    </Container>
  );
};

export default FunderEdit;
